import type { EventType } from '../redux/api/eventsApi';

export type EventCategory =
  | 'preventative_maintenance'
  | 'corrective_maintenance'
  | 'asset_update'
  | 'upcoming'
  | 'other';

export interface EventCategoryConfig {
  label: string;
  color: string;
  iconName: string;
}

export const EVENT_CATEGORY_CONFIG: Record<EventCategory, EventCategoryConfig> = {
  preventative_maintenance: {
    label: 'Preventative Maintenance',
    color: '#3B82F6',
    iconName: 'Wrench',
  },
  corrective_maintenance: {
    label: 'Corrective Maintenance',
    color: '#EF4444',
    iconName: 'AlertTriangle',
  },
  asset_update: {
    label: 'Asset Update',
    color: '#8B5CF6',
    iconName: 'RefreshCw',
  },
  upcoming: {
    label: 'Upcoming',
    color: '#F59E0B',
    iconName: 'CalendarClock',
  },
  other: {
    label: 'Other',
    color: '#6B7280',
    iconName: 'Circle',
  },
};

/**
 * Derives an event category from the event_type and occurred_at fields.
 * Used as a fallback when the backend doesn't provide `event_category`.
 *
 * Rules:
 * - PM: maintenance, inspection
 * - CM: outage, incident
 * - upcoming: future-dated events, warranty (if future)
 * - other: weather, pest, vegetation, default
 */
export function deriveEventCategory(
  eventType: string,
  occurredAt?: string | null,
): EventCategory {
  const type = eventType?.toLowerCase();

  // Check if event is in the future
  const isFuture = occurredAt ? new Date(occurredAt) > new Date() : false;

  // Upcoming: future-dated events or future warranty
  if (isFuture) return 'upcoming';

  // PM: maintenance, inspection
  if (type === 'maintenance' || type === 'inspection') {
    return 'preventative_maintenance';
  }

  // CM: outage, incident
  if (type === 'outage' || type === 'incident') {
    return 'corrective_maintenance';
  }

  // Warranty in the past = other
  if (type === 'warranty') return 'other';

  // weather, pest, vegetation, anything else
  return 'other';
}

export const ALL_EVENT_CATEGORIES: EventCategory[] = [
  'preventative_maintenance',
  'corrective_maintenance',
  'asset_update',
  'upcoming',
  'other',
];
